import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faMicrophone,
  faMicrophoneSlash,
  faForwardStep,
} from "@fortawesome/free-solid-svg-icons";
import SpeakerOnIcon from "./icons/SpeakerOnIcon";
import SpeakerOffIcon from "./icons/SpeakerOffIcon";
import SendChatIcon from "./icons/SendChatIcon";

function WaveStyle({
  handleStopTTS,
  displayedText,
  question,
  handleInputChange,
  handleKeyDown,
  handleInputFocus,
  handleSendMessage,
  isLoading,
  isTyping,
  handleSkipTyping,
  messagesContainerRef,
  isTTSEnabled,
  setIsTTSEnabled,
  handleVoiceInput,
  isListening,
  // Add these props to detect audio/talking state
  isAudioPlaying,
  isAudioPlayingRef,
}) {
  const [placeholderText, setPlaceholderText] = useState("Ask me a question! 😊");
  const [showStartMessage, setShowStartMessage] = useState(true);
  const [messageReceived, setMessageReceived] = useState(false);

  useEffect(() => {
    if (displayedText) {
      setShowStartMessage(false);
      setMessageReceived(true);
    }
  }, [displayedText]);

  useEffect(() => {
    if (messagesContainerRef?.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [displayedText]);

  const handleTTSButtonClick = () => {
    try {
      handleStopTTS();
      handleSkipTyping();
      setIsTTSEnabled((prev) => !prev);
    } catch (error) {
      console.error("Error in TTS button click:", error);
    }
  };

  const handleFastForward = () => {
    try {
      handleStopTTS();
      handleSkipTyping();
    } catch (error) {
      console.error("Error in fast forward:", error);
    }
  };

  const handleInputSendMessage = () => {
    try {
      handleSendMessage();
      setMessageReceived(false);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleSafeVoiceInput = () => {
    try {
      if (!isLoading && !isTyping) {
        handleVoiceInput();
      }
    } catch (error) {
      console.error("Error in voice input:", error);
    }
  };

  const handleSafeInputChange = (e) => {
    try {
      if (!isTyping) {
        handleInputChange(e);
      }
    } catch (error) {
      console.error("Error in input change:", error);
    }
  };

  // Enhanced Siri-like Animation Component
  const SiriAnimation = () => {
    // Determine if the assistant is actively talking/responding
    const isTalking = isTyping || isAudioPlaying || (isAudioPlayingRef?.current);

    return (
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        width: "100%",
        height: "100%",
        padding: "20px"
      }}>
        <img 
          src={isTalking 
            ? "https://i.imgur.com/hUT8QOs.gif"  // Pulsating GIF when talking
            : "https://i.imgur.com/OYu7dWR.gif"  // Original GIF when inactive
          }
          alt="Siri Animation"
          style={{
            width: "450px",
            height: "450px", 
            maxWidth: "90%",
            maxHeight: "90%",
            objectFit: "contain",
            transition: "filter 1.2s ease-in-out",
          }}
        />
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 flex flex-col"
      style={{ 
        backgroundColor: "#73B9AF", 
        margin: 0, 
        padding: 0,
        overflow: "hidden"
      }}
    >
      {/* Top Bar for Icons */}
      <div className="flex items-center justify-between p-4 relative z-10">
        <div className="flex items-center gap-8">
          <button
            onClick={handleTTSButtonClick}
            className="p-0 transition duration-300"
            aria-label={isTTSEnabled ? "Mute TTS" : "Unmute TTS"}
            style={{ background: "none", border: "none" }}
          >
            {isTTSEnabled ? <SpeakerOnIcon /> : <SpeakerOffIcon />}
          </button>
        </div>
      </div>

      {/* Animation Area */}
      <div
        className="absolute inset-0 w-full h-[50%] flex flex-col overflow-hidden"
        style={{ backgroundColor: "#73B9AF" }}
      >
        <div
          className="absolute top-0 left-0 w-full h-full flex items-center justify-center"
          style={{ 
            alignSelf: "center",
            zIndex: 1
          }}
        >
          <SiriAnimation />
        </div>
      </div>

      {/* Bottom Section: Chat Area and Buttons */}
      <div
        className="absolute bottom-0 w-full flex flex-col items-center justify-start p-8 z-10"
        style={{ backgroundColor: "#ECECEC", height: "50%" }}
      >
        <div
          ref={messagesContainerRef}
          style={{
            maxHeight: "1000px",
            overflowY: "auto",
            width: "100%",
            padding: "8px",
          }}
        >
          {/* Displayed Text */}
          {displayedText && (
            <div
              className="p-4 bg-[#73B9AF] text-black text-lg md:text-xl rounded-lg shadow-md break-words cursor-pointer"
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "20px",
                fontWeight: "500",
                lineHeight: "normal",
                color: "#000",
                textAlign: "center",
                background: "#73B9AF",
                backdropFilter: "blur(30px)",
                marginBottom: "16px",
                width: "100%",
              }}
              onClick={() => {
                try {
                  handleSkipTyping();
                } catch (error) {
                  console.error("Error in skip typing:", error);
                }
              }}
            >
              <span>{displayedText}</span>
            </div>
          )}

          {/* Start Message */}
          {showStartMessage && (
            <div
              className="p-4 bg-[#73B9AF] text-black text-lg md:text-xl rounded-lg shadow-md mb-4 w-full text-center"
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "20px",
                fontWeight: "500",
                lineHeight: "normal",
                color: "#000",
                background: "#73B9AF",
                backdropFilter: "blur(30px)",
              }}
            >
              Welcome! Whenever you're ready, let's dive into your questions and start exploring.
            </div>
          )}
        </div>

        {/* Input and Send Button */}
        <div
          className="flex items-center w-full relative"
          style={{
            position: "fixed",
            bottom: "32px",
            left: "24px",
            right: "24px",
            maxWidth: "calc(100% - 48px)",
            zIndex: 20
          }}
        >
          <input
            value={question || ""}
            onChange={handleSafeInputChange}
            onKeyDown={(e) => {
              try {
                handleKeyDown(e);
              } catch (error) {
                console.error("Error in key down:", error);
              }
            }}
            onFocus={() => {
              try {
                handleInputFocus();
              } catch (error) {
                console.error("Error in input focus:", error);
              }
            }}
            className="flex-grow h-12 pl-12 pr-10 rounded-full text-xl focus:outline-none transition duration-300"
            type="text"
            placeholder={placeholderText}
            readOnly={isTyping}
            style={{ 
              color: "#333", 
              backgroundColor: "#FFFFFF",
              border: "1px solid #ddd"
            }}
          />
          
          <button
            onClick={handleSafeVoiceInput}
            className={`absolute left-3 inset-y-0 my-auto h-8 w-8 flex items-center justify-center rounded-full bg-transparent ${
              !isLoading && !isTyping ? "text-black" : "text-gray-400"
            } ${isListening ? "pulsating" : ""}`}
            aria-label="Voice Input"
            disabled={isLoading || isTyping}
            style={{ 
              cursor: !isLoading && !isTyping ? "pointer" : "not-allowed",
              background: "none",
              border: "none"
            }}
          >
            <FontAwesomeIcon
              icon={isListening ? faMicrophone : faMicrophoneSlash}
              style={{ 
                color: !isLoading && !isTyping ? "#000" : "#999", 
                fontSize: "16px" 
              }}
            />
          </button>

          <button
            onClick={
              isLoading
                ? null
                : isTyping && messageReceived
                ? handleFastForward
                : handleInputSendMessage
            }
            className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center"
            disabled={
              isLoading || (isTyping && !messageReceived) || (!question?.trim() && !isTyping)
            }
            aria-label={isTyping && messageReceived ? "Skip Typing" : "Send Message"}
            style={{ 
              background: "none",
              border: "none"
            }}
          >
            {isLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : isTyping && messageReceived ? (
              <FontAwesomeIcon icon={faForwardStep} style={{ fontSize: "24px" }} />
            ) : (
              <SendChatIcon />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WaveStyle;